import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { normalizeNodeId } from '@gt/para/shared/para-node-id.util';
import { ParaAssessmentSeriesService, type ParaAssessmentSeriesDto, type ParaAssessmentSeriesItemDto } from './para-assessment-series.service';
import { ParaOrgStateService } from './para-org-state.service';

@Injectable({ providedIn: 'root' })
export class ParaSelectedAssessmentService {
	private readonly selectedAssessmentIdSubject = new BehaviorSubject<string | null>(null);

	readonly selectedAssessmentId$: Observable<string | null> = this.selectedAssessmentIdSubject.asObservable().pipe(
		map((v) => (v != null && String(v).trim() !== '' ? String(v).trim() : null)),
		distinctUntilChanged(),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/** Emits only once a non-empty assessment id is resolved (from series or explicit user selection). */
	readonly resolvedAssessmentId$: Observable<string> = this.selectedAssessmentId$.pipe(
		filter((id): id is string => id != null && String(id).trim() !== ''),
		distinctUntilChanged(),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly activeSeriesItem$: Observable<ParaAssessmentSeriesItemDto | null> = combineLatest([
		this.paraSeries.selectedNodeAssessmentSeries$,
		this.resolvedAssessmentId$,
	]).pipe(
		map(([s, id]) => {
			if (!s || !id) return null;
			return s.series.find((x) => String(x.assessmentId) === id) ?? null;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly unlockLabel$: Observable<string | null> = this.paraSeries.selectedNodeAssessmentSeries$.pipe(
		map((s) => {
			if (!s?.unlock) return null;
			const u = s.unlock;
			return u.isUnlocked ? 'Unlocked' : `${u.completedCount ?? 0}/${u.requiredCount ?? 0} complete`;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	constructor(
		private readonly paraSeries: ParaAssessmentSeriesService,
		private readonly paraOrgState: ParaOrgStateService
	) {
		this.paraOrgState.selectedParaNodeEntity$
			.pipe(
				map((n) => normalizeNodeId(n?.id)),
				distinctUntilChanged()
			)
			.subscribe(() => {
				this.selectedAssessmentIdSubject.next(null);
			});

		this.paraSeries.selectedNodeAssessmentSeries$.subscribe((s) => {
			this.applySeriesSelection(s);
		});
	}

	static assessmentIdFromSeries(series: ParaAssessmentSeriesDto | null | undefined): string | null {
		if (!series) return null;
		const selected = series.selectedAssessmentId != null ? String(series.selectedAssessmentId).trim() : '';
		if (selected) return selected;
		const first = series.series?.[0]?.assessmentId;
		return first != null && String(first).trim() !== '' ? String(first).trim() : null;
	}

	/** Apply server series selection after scope or series changes. */
	syncFromSeries(series: ParaAssessmentSeriesDto | null | undefined): void {
		this.applySeriesSelection(series ?? null);
	}

	select(assessmentId: string | null): void {
		const next = assessmentId != null && assessmentId.trim() !== '' ? assessmentId.trim() : 'para-ce-screener';
		this.selectedAssessmentIdSubject.next(next);
	}

	private applySeriesSelection(s: ParaAssessmentSeriesDto | null): void {
		const fallback = ParaSelectedAssessmentService.assessmentIdFromSeries(s);
		const current = this.selectedAssessmentIdSubject.value;
		const seriesIds = (s?.series ?? []).map((x) => String(x.assessmentId));

		if ((current == null || current.trim() === '') && fallback) {
			this.selectedAssessmentIdSubject.next(fallback);
			return;
		}
		if (current && seriesIds.length && !seriesIds.includes(current)) {
			this.selectedAssessmentIdSubject.next(fallback || seriesIds[0] || null);
		}
	}
}
